/**
 * Client for the server-side "lifecycle intents" (epic tv-1p2) — atomic
 * transactions the server owns, replacing multi-PUT client flows that could tear
 * (a viewer catching a half-applied write). Every wrapper mirrors the fetch/auth
 * shape of persist.ts (reusing its `authHeaders`), never throws, and returns a
 * discriminated result the caller branches on:
 *
 * - success `{ ok: true, ... }`
 * - failure `{ ok: false, kind, status, error, code?, message? }` where `kind`
 *   is what a desk flow switches on (see intentErrorKind):
 *     'slot_taken' — 409 firm-slot conflict: tell staff the slot was just taken;
 *     'rejected'   — the server refused this payload (4xx): retrying won't help;
 *     'transient'  — never reached the server / server fault (status 0, 5xx,
 *                    408, 429): safe to retry with the SAME ids (idempotent).
 *
 * One clock (tv-2na.10.5): every wrapper sends the facilitator's sim clock as
 * `nowIso` (a caller-supplied `nowIso` wins). The server honours it only in a
 * demo/dev deployment, so paidAt / ledger stamps match the demo's "today".
 *
 * Only reached in the DB-authoritative served build (apiMode's store-fallback is
 * OFF); the store-fallback path keeps the client-side writes and never calls these.
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { useSimulationStore } from '@/state/simulationStore';
import type { Booking, ConversationEntry, CustomerRecord, Deposit, TopupPlan } from '@/domain/types';
import type { NewDateReason } from '@atink/domain';

/** How a failed intent should be handled (see the file header). */
export type IntentErrorKind = 'slot_taken' | 'rejected' | 'transient';

/** A failed intent. `status` is the HTTP code (0 = network/abort), `error` the
 *  server's error (`booking-conflict` / `id-conflict` / `domain-validation` /
 *  `invalid-record` / `invalid payload` / `not-actionable` / … or `network` /
 *  `http-<status>`), `code` its machine-readable sub-code when present
 *  (`slot_taken` / `id_conflict` / a validator code / `foreign_key` …). */
export type IntentFailure = {
  ok: false;
  kind: IntentErrorKind;
  status: number;
  error: string;
  code?: string;
  message?: string;
};

/** Classify a failed intent response. */
export function intentErrorKind(status: number, body?: { error?: string; code?: string }): IntentErrorKind {
  if (status === 0 || status === 408 || status === 429 || status >= 500) return 'transient';
  if (status === 409 && (body?.code === 'slot_taken' || body?.error === 'booking-conflict')) {
    return 'slot_taken';
  }
  return 'rejected';
}

/** Fold any intent result to a single switchable outcome. */
export function intentOutcome(r: { ok: true } | IntentFailure): 'ok' | IntentErrorKind {
  return r.ok ? 'ok' : r.kind;
}

/**
 * Staff-facing copy for a failed desk intent (tv-2na.8), so every flow says the
 * same true thing. `retry`: re-sending the SAME ids is safe (idempotent) and may
 * succeed. Deliberately never "API offline" or "nothing was charged" — at the
 * desk the card/cash may already have been taken; what failed is the RECORD.
 */
export function intentFailureCopy(
  f: Pick<IntentFailure, 'error'> & Partial<Pick<IntentFailure, 'ok' | 'kind' | 'code' | 'message' | 'status'>>,
): { title: string; body: string; retry: boolean } {
  const kind = f.kind ?? intentErrorKind(f.status ?? 0, f);
  if (kind === 'slot_taken') {
    return {
      title: 'That slot was just taken',
      body: 'Someone booked that time a moment ago, so this booking and payment were not recorded. Pick another time.',
      retry: false,
    };
  }
  if (kind === 'transient') {
    return {
      title: "Didn't save — try again",
      body: "We couldn't reach the server, so the booking and payment weren't saved. Retry — it's safe, it can't record twice.",
      retry: true,
    };
  }
  // D05-11: no money is taken without the client's email on file.
  if (f.error === 'email-required') {
    return {
      title: "Add the client's email",
      body: 'An email is needed to take a deposit. Add it and take the payment again. The payment was not taken.',
      retry: false,
    };
  }
  // A server message is a full sentence ("...from now on."), so drop its own
  // closing full stop before this copy adds one (tv-2na.10.10).
  const reason = (f.message ?? (f.code && f.code !== f.error ? `${f.error} (${f.code})` : f.error)).replace(/\.+$/, '');
  return {
    title: "Couldn't save the booking",
    body: `The server refused it: ${reason}. The booking and payment were not recorded.`,
    retry: false,
  };
}

/** The facilitator's sim clock — the "now" the demo shows (tv-2na.10.5). */
function simNowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/** POST an intent; resolves to the parsed 2xx body or a typed failure. */
async function postIntent(
  url: string,
  payload: Record<string, unknown>,
): Promise<{ ok: true; body: Record<string, unknown> } | IntentFailure> {
  if (isSigningIn()) await whenSignedIn(); // B3: never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? simNowIso() }),
    });
  } catch {
    // Network error / abort — never reached the server; no HTTP status.
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }

  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown> | null;
    return { ok: true, body: body ?? {} };
  }

  // Non-2xx: surface the server's error + code + message for the caller's toast.
  const body = (await res.json().catch(() => undefined)) as
    | { error?: string; code?: string; message?: string }
    | undefined;
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}

const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

// ── pay-deposit ──────────────────────────────────────────────────────────────

/**
 * `POST /api/intents/pay-deposit` — one server transaction that flips the held
 * booking held -> confirmed (is_firm true) AND the deposit pending -> paid, or
 * neither. `alreadyPaid` is true for an idempotent replay (still a success).
 */
export type PayDepositResult = { ok: true; alreadyPaid: boolean } | IntentFailure;

/** A desk / phone payment (not the Stripe link): who took it and how, for the
 *  ledger + deposit (tv-2na.8.11). Omitted = the Stripe link. */
export interface PayDepositPayment {
  by?: string;
  note?: string;
  channel?: string;
  method?: string;
}

export async function payDepositIntent(
  depositId: string,
  /** `contactEmail` (D05-11): a desk / phone payment's client email, filling an empty one on file. */
  opts: { nowIso?: string; payment?: PayDepositPayment; contactEmail?: string } = {},
): Promise<PayDepositResult> {
  const r = await postIntent('/api/intents/pay-deposit', { depositId, ...opts });
  if (!r.ok) return r;
  return { ok: true, alreadyPaid: r.body.alreadyPaid === true };
}

// ── create-and-pay ───────────────────────────────────────────────────────────

/**
 * The payload for `POST /api/intents/create-and-pay` (tv-rp7.2 / W1): the fresh
 * booking + deposit (the same domain shapes the write-through bridge ships), the
 * payment, and optionally a BRAND-NEW customer and a top-up plan — all landing in
 * ONE transaction. Ids are client-minted and idempotent: re-sending the same
 * payload after a transient failure is a safe no-op success. The server OWNS the
 * lifecycle end state (booking confirmed + firm, never confirmedAt; deposit paid;
 * pair cross-referenced), so any lifecycle fields sent are overridden.
 *
 * `customer`: created only if its id is not on file (an existing profile is never
 * overwritten); its id must equal booking.customerId and deposit.customerId.
 * Suppress the bridge PUT for it (withServerAuthoredWrites) like the pair.
 */
/** A brief's NEW multi-session piece (tv-2na.11.22): the server creates it as
 *  `piece-<depositId>` and links the booking in the same transaction. */
export interface NewPiecePayload {
  title: string;
  estimatedTotalSessions: number;
  style?: string;
  placement?: string;
  designType?: string;
}

export interface CreateAndPayPayload {
  bookingId: string;
  depositId: string;
  booking: Booking;
  deposit: Deposit;
  payment: { amountAud: number; by?: string; note?: string; channel?: string; at?: string };
  customer?: CustomerRecord;
  topupPlan?: TopupPlan;
  newPiece?: NewPiecePayload;
  /** D05-11: the client's email from the take-payment form; fills an empty one on file. */
  contactEmail?: string;
  /** Override the sim clock stamp (defaults to the facilitator's clock). */
  nowIso?: string;
}

/**
 * Outcome of create-and-pay. On success `alreadyExists` (=== `alreadyPaid`) is
 * true for an idempotent replay — the FIRST call already committed and ran the
 * side-effects. A 409 is either `kind: 'slot_taken'` (firm-slot conflict) or
 * `kind: 'rejected'` with `code: 'id_conflict'` (the deposit id is already paid
 * for a different booking — never a double pay).
 */
export type CreateAndPayResult =
  | { ok: true; alreadyExists: boolean; alreadyPaid: boolean; customerId?: string; topupPlanId?: string }
  | IntentFailure;

export async function createAndPayIntent(payload: CreateAndPayPayload): Promise<CreateAndPayResult> {
  const r = await postIntent('/api/intents/create-and-pay', { ...payload });
  if (!r.ok) return r;
  const replay = r.body.alreadyExists === true || r.body.alreadyPaid === true;
  return {
    ok: true,
    alreadyExists: replay,
    alreadyPaid: replay,
    ...(str(r.body.customerId) ? { customerId: r.body.customerId } : {}),
    ...(str(r.body.topupPlanId) ? { topupPlanId: r.body.topupPlanId } : {}),
  };
}

// ── hold-slot ────────────────────────────────────────────────────────────────

/**
 * The payload for `POST /api/intents/hold-slot` (tv-3ps.7): the fresh HELD
 * booking + its PENDING deposit, and optionally a brand-new customer and the
 * deposit's top-up plan (tv-2na.7 — the plan travels INSIDE the intent instead of
 * a separate PUT racing the deposit FK). The server forces the hold end state and
 * lands everything in ONE transaction, or nothing.
 */
export interface HoldSlotPayload {
  bookingId: string;
  depositId: string;
  booking: Booking;
  deposit: Deposit;
  customer?: CustomerRecord;
  topupPlan?: TopupPlan;
  newPiece?: NewPiecePayload;
  /** L1: the server texts the client the pay link in the hold's commit (the Sweeper gap-fill). */
  textLink?: boolean;
  nowIso?: string;
}

/** Outcome of hold-slot. `alreadyExists`: the deposit id was already on file (a
 *  replay) — a no-op success. */
export type HoldSlotResult =
  | { ok: true; alreadyExists: boolean; customerId?: string; topupPlanId?: string }
  | IntentFailure;

export async function holdSlotIntent(payload: HoldSlotPayload): Promise<HoldSlotResult> {
  const r = await postIntent('/api/intents/hold-slot', { ...payload });
  if (!r.ok) return r;
  return {
    ok: true,
    alreadyExists: r.body.alreadyExists === true,
    ...(str(r.body.customerId) ? { customerId: r.body.customerId } : {}),
    ...(str(r.body.topupPlanId) ? { topupPlanId: r.body.topupPlanId } : {}),
  };
}

// ── pay-topup-instalment ─────────────────────────────────────────────────────

/**
 * `POST /api/intents/pay-topup-instalment` (tv-rp7.4): pay the earliest unpaid
 * instalment in ONE transaction. The first $50+ secures the deposit (pending ->
 * paid, part-paid) and firms the held booking (tv-2na.8.10: a partial is a REAL
 * booking); the FINAL one completes the plan. `complete`: this was the final
 * instalment; `bookingConfirmed`: this pay confirmed the booking; `depositSecured`:
 * this pay moved the deposit pending -> paid; `alreadyComplete`: idempotent replay.
 */
export type PayTopupInstalmentResult =
  | { ok: true; complete: boolean; bookingConfirmed: boolean; depositSecured: boolean; alreadyComplete: boolean }
  | IntentFailure;

export async function payTopupInstalmentIntent(
  planId: string,
  opts: { nowIso?: string } = {},
): Promise<PayTopupInstalmentResult> {
  const r = await postIntent('/api/intents/pay-topup-instalment', { planId, ...opts });
  if (!r.ok) return r;
  return {
    ok: true,
    complete: r.body.complete === true,
    bookingConfirmed: r.body.bookingConfirmed === true,
    depositSecured: r.body.depositSecured === true,
    alreadyComplete: r.body.alreadyComplete === true,
  };
}

// ── apply-deposit (tv-2na.8.17) ──────────────────────────────────────────────

/**
 * `POST /api/intents/apply-deposit` — part of the session's deposit is applied to
 * today's balance and the REST follows the booking (DECIDED 24 Sep, Q1), in ONE
 * server transaction: 'rolls-to-rebook' (the next session is created in the same
 * tx and the rest rolls onto it), 'used-up' (final session / one-off: no credit),
 * or 'on-file' (mid-piece, no rebook: a dateless deposit valid 12 months, for the
 * Sweeper). `remainderDepositId` is client-minted: a replay is a no-op success.
 */
export interface ApplyDepositPayload {
  sourceBookingId: string;
  appliedAud: number;
  remainderCase: 'rolls-to-rebook' | 'used-up' | 'on-file';
  remainderDepositId: string;
  rebook?: { bookingId: string; booking: Booking };
  nowIso?: string;
}

export type ApplyDepositResult =
  | {
      ok: true;
      alreadyApplied: boolean;
      appliedAud: number;
      remainderAud: number;
      remainderDepositId?: string;
      bookingId?: string;
    }
  | IntentFailure;

export async function applyDepositIntent(payload: ApplyDepositPayload): Promise<ApplyDepositResult> {
  const r = await postIntent('/api/intents/apply-deposit', payload as unknown as Record<string, unknown>);
  if (!r.ok) return r;
  return {
    ok: true,
    alreadyApplied: r.body.alreadyApplied === true,
    appliedAud: Number(r.body.appliedAud ?? 0),
    remainderAud: Number(r.body.remainderAud ?? 0),
    ...(str(r.body.remainderDepositId) ? { remainderDepositId: r.body.remainderDepositId } : {}),
    ...(str(r.body.bookingId) ? { bookingId: r.body.bookingId } : {}),
  };
}

// ── resend-link (W5b #2, tv-2na.8.20) ────────────────────────────────────────

/**
 * `POST /api/intents/resend-link`: re-send an expired unpaid link in ONE server
 * transaction — the fresh held booking + pending deposit (or a dateless pending
 * deposit) AND the old link superseded, or nothing. The server re-runs the pure
 * resendDecision under the old deposit's lock. The fresh deposit carries the
 * RESENDER as its sentByRole / sentBy (Q3).
 */
export interface ResendLinkPayload {
  oldDepositId: string;
  depositId: string;
  deposit: Deposit;
  bookingId?: string;
  booking?: Booking;
  via?: 'sweeper';
  nowIso?: string;
}

/**
 * The switchable outcome. `needs-new-date`: the slot passed / was taken (Q1),
 * nothing written. `not-resendable`: money in / moved on / live / window closed.
 * `already-resent`: another fresh link beat this one. `failed`: transport or an
 * unexpected refusal (see `failure`).
 */
export type ResendLinkResult =
  | { ok: true; outcome: 'ok'; alreadyExists: boolean; freshDepositId: string; freshBookingId?: string }
  | { ok: false; outcome: 'needs-new-date'; reason: NewDateReason }
  | { ok: false; outcome: 'not-resendable'; reason: string }
  | { ok: false; outcome: 'already-resent' }
  | { ok: false; outcome: 'failed'; failure: IntentFailure };

/**
 * 7b6n: `POST /api/intents/no-deposit-hold` — a $0 booking (held + request, or
 * confirmed + waived) and a new walk-in customer, in ONE transaction.
 */
export async function noDepositHoldIntent(payload: {
  booking: Booking;
  request: { id: string; status: 'pending' | 'approved'; requestedBy: string; note: string; createdAt: string; resolvedBy?: string; resolvedAt?: string };
  customer?: CustomerRecord;
}): Promise<{ ok: true; alreadyExists: boolean } | IntentFailure> {
  const r = await postIntent('/api/intents/no-deposit-hold', { ...payload });
  if (!r.ok) return r;
  return { ok: true, alreadyExists: r.body.alreadyExists === true };
}

/**
 * deposits-10: `POST /api/intents/supersede-link` — close an old expired link
 * against the fresh one "Pick a new date" just sent, so the old row stops
 * offering RESEND. Idempotent; no message is sent.
 */
export async function supersedeLinkIntent(
  oldDepositId: string,
  freshDepositId: string,
): Promise<{ ok: true; releasedBookingId?: string } | IntentFailure> {
  const r = await postIntent('/api/intents/supersede-link', { oldDepositId, freshDepositId });
  if (!r.ok) return r;
  return { ok: true, ...(str(r.body.releasedBookingId) ? { releasedBookingId: r.body.releasedBookingId } : {}) };
}

export async function resendLinkIntent(payload: ResendLinkPayload): Promise<ResendLinkResult> {
  const r = await postIntent('/api/intents/resend-link', { ...payload });
  if (r.ok) {
    return {
      ok: true,
      outcome: 'ok',
      alreadyExists: r.body.alreadyExists === true,
      freshDepositId: str(r.body.freshDepositId) ? r.body.freshDepositId : payload.depositId,
      ...(str(r.body.freshBookingId) ? { freshBookingId: r.body.freshBookingId } : {}),
    };
  }
  if (r.error === 'needs-new-date') {
    return { ok: false, outcome: 'needs-new-date', reason: r.code === 'taken' ? 'taken' : r.code === 'no-date' ? 'no-date' : 'past' };
  }
  // A race the server caught at the write: the slot went firm (409) or started (422).
  if (r.kind === 'slot_taken') return { ok: false, outcome: 'needs-new-date', reason: 'taken' };
  if (r.code === 'start_in_past') return { ok: false, outcome: 'needs-new-date', reason: 'past' };
  if (r.error === 'not-resendable') return { ok: false, outcome: 'not-resendable', reason: r.code ?? 'unknown' };
  if (r.error === 'already-resent') return { ok: false, outcome: 'already-resent' };
  return { ok: false, outcome: 'failed', failure: r };
}

// ── place-deposit (W5b D25, tv-2na.9.11) ─────────────────────────────────────

/**
 * `POST /api/intents/place-deposit`: place a PAID deposit on file into a slot —
 * the confirmed booking AND the deposit's bookingId in ONE transaction (the old
 * client flow PUT the deposit before its booking existed: live 500s). The server
 * forces the booking solid (confirmed, is_firm, never confirmedAt).
 * `alreadyPlaced`: an idempotent replay of the same placement.
 */
export type PlaceDepositResult = { ok: true; alreadyPlaced: boolean; bookingId?: string } | IntentFailure;

export async function placeDepositIntent(payload: {
  depositId: string;
  bookingId: string;
  booking: Booking;
  /** Who placed it (B4): a Sweeper gap placement is confirmed on the call; an Agent's keeps the cadence. */
  via?: 'sweeper' | 'agent';
  nowIso?: string;
}): Promise<PlaceDepositResult> {
  const r = await postIntent('/api/intents/place-deposit', { ...payload });
  if (!r.ok) return r;
  return {
    ok: true,
    alreadyPlaced: r.body.alreadyPlaced === true,
    ...(typeof r.body.bookingId === 'string' ? { bookingId: r.body.bookingId } : {}),
  };
}

/**
 * POST /api/intents/reinstate-deposit (B4, 75ng): Ops / the owner give a
 * forfeited deposit back with a typed reason. The server records who (from the
 * token) and why, and puts it back on file for 12 months.
 */
export type ReinstateDepositResult = { ok: true; amountAud: number; expiresAt: string; at: string } | IntentFailure;

export async function reinstateDepositIntent(depositId: string, reason: string): Promise<ReinstateDepositResult> {
  const r = await postIntent('/api/intents/reinstate-deposit', { depositId, reason });
  if (!r.ok) return r;
  return {
    ok: true,
    amountAud: Number(r.body.amountAud ?? 0),
    expiresAt: String(r.body.expiresAt ?? ''),
    at: String(r.body.at ?? ''),
  };
}

/**
 * POST /api/intents/enquiry-intake (D01-02): a website form enquiry, through the
 * staff-only intake (there is no public endpoint: the studios' forms send emails,
 * ingested later; Anas 7 Oct). The console's Customer Simulator uses it.
 */
export async function enquiryIntakeIntent(payload: {
  studio: string;
  name: string;
  mobile: string;
  email?: string;
  message: string;
  preferredDate?: string;
  submissionId?: string;
  nowIso?: string;
}): Promise<{ ok: true } | IntentFailure> {
  const r = await postIntent('/api/intents/enquiry-intake', payload);
  return r.ok ? { ok: true } : r;
}

/**
 * POST /api/intents/topup-move-date (B4, 75my): outside 7 days, hold the new
 * date as a ghost until a >= $50 partial is paid. Records no money.
 */
export type TopupMoveResult =
  | {
      ok: true;
      ghostBookingId: string;
      partialAud: number;
      lapsesAt: string;
      alreadyPending: boolean;
      instalments: Array<{ dueIso: string; amountAud: number; status: string; paidAt?: string }>;
      /** L5: card-phone took the partial and the new date is the booking now. */
      landed: boolean;
      /** L5: text-link texted the client the pay link (false: no mobile on file). */
      texted?: boolean;
    }
  | IntentFailure;

/** L5 (D02-23): how the Agent takes the move's partial — card over the phone now, or a texted pay link. */
export type TopupMovePay = 'card-phone' | 'text-link';

export async function topupMoveDateIntent(
  planId: string,
  newStartIso: string,
  pay?: TopupMovePay,
  /** D05-11: card-phone is a staff-taken pay — the client's email (fills an empty one on file). */
  contactEmail?: string,
): Promise<TopupMoveResult> {
  const r = await postIntent('/api/intents/topup-move-date', {
    planId,
    newStartIso,
    ...(pay ? { pay } : {}),
    ...(contactEmail ? { contactEmail } : {}),
  });
  if (!r.ok) return r;
  return {
    ok: true,
    ghostBookingId: String(r.body.ghostBookingId ?? ''),
    partialAud: Number(r.body.partialAud ?? 0),
    lapsesAt: String(r.body.lapsesAt ?? ''),
    alreadyPending: r.body.alreadyPending === true,
    instalments: Array.isArray(r.body.instalments) ? (r.body.instalments as TopupMoveResult extends { instalments: infer I } ? I : never) : [],
    landed: r.body.landed === true,
    ...(typeof r.body.texted === 'boolean' ? { texted: r.body.texted } : {}),
  };
}

/**
 * POST /api/intents/topup-extend-payby (tv-toef): Ops (or the owner) extends a
 * top-up plan's pay-by. The server moves the current instalment out, re-arms it
 * and resets the chase count; a plain client write can't reset the count.
 */
export type TopupExtendResult =
  | {
      ok: true;
      previousDueIso: string;
      newDueIso: string;
      instalments: Array<{ dueIso: string; amountAud: number; status: string; paidAt?: string }>;
    }
  | IntentFailure;

export async function topupExtendPayByIntent(planId: string, newDueIso: string): Promise<TopupExtendResult> {
  const r = await postIntent('/api/intents/topup-extend-payby', { planId, newDueIso });
  if (!r.ok) return r;
  return {
    ok: true,
    previousDueIso: String(r.body.previousDueIso ?? ''),
    newDueIso: String(r.body.newDueIso ?? newDueIso),
    instalments: Array.isArray(r.body.instalments) ? (r.body.instalments as TopupExtendResult extends { instalments: infer I } ? I : never) : [],
  };
}

/**
 * POST /api/intents/topup-move-request (D01-17): inside 7 days a top-up move is
 * Ops' call, so the Agent sends it to Ops with the date the client asked for.
 * Nothing moves; the plan carries the request for Ops' Top-up extensions list.
 */
export type TopupMoveRequestResult =
  | { ok: true; requestedStartIso: string; requestedBy: string; requestedAt: string; requestedFromIso: string }
  | IntentFailure;

export async function topupMoveRequestIntent(planId: string, requestedStartIso: string): Promise<TopupMoveRequestResult> {
  const r = await postIntent('/api/intents/topup-move-request', { planId, requestedStartIso });
  if (!r.ok) return r;
  return {
    ok: true,
    requestedStartIso: String(r.body.requestedStartIso ?? requestedStartIso),
    requestedBy: String(r.body.requestedBy ?? ''),
    requestedAt: String(r.body.requestedAt ?? ''),
    requestedFromIso: String(r.body.requestedFromIso ?? ''),
  };
}

/** POST /api/intents/topup-move-request-clear (D01-17): Ops closes a move request. */
export async function topupMoveRequestClearIntent(planId: string): Promise<{ ok: true } | IntentFailure> {
  const r = await postIntent('/api/intents/topup-move-request-clear', { planId });
  return r.ok ? { ok: true } : r;
}

/**
 * POST /api/intents/hold-self-book-slot (B5, 7fr8): an artist's pin becomes a
 * real server hold (held booking + pending deposit, on the link window).
 */
export type HoldSelfBookResult =
  | { ok: true; bookingId: string; depositId: string; depositAud: number; durationMin: number; expiresAt: string; overbookable: boolean }
  | IntentFailure;

export async function holdSelfBookSlotIntent(payload: {
  token: string;
  startIso: string;
  session: 'small' | 'half-day' | 'full-day';
  custom?: { depositAud: number; durationMin: number };
  depositReference: string;
  depositId: string;
  bookingId: string;
}): Promise<HoldSelfBookResult> {
  const r = await postIntent('/api/intents/hold-self-book-slot', { ...payload });
  if (!r.ok) return r;
  return {
    ok: true,
    bookingId: String(r.body.bookingId ?? payload.bookingId),
    depositId: String(r.body.depositId ?? payload.depositId),
    depositAud: Number(r.body.depositAud ?? 0),
    durationMin: Number(r.body.durationMin ?? 0),
    expiresAt: String(r.body.expiresAt ?? ''),
    overbookable: r.body.overbookable === true,
  };
}

/**
 * POST /api/intents/send-self-book-link (B5, 8xar): the server stamps a named
 * link's clock, and chains a fresh link to the client's expired one.
 */
export type SendSelfBookResult =
  | { ok: true; token: string; generatedAt: string; expiresAt: string; replacesLinkId?: string }
  | IntentFailure;

export async function sendSelfBookLinkIntent(link: Record<string, unknown>): Promise<SendSelfBookResult> {
  const r = await postIntent('/api/intents/send-self-book-link', { link });
  if (!r.ok) return r;
  return {
    ok: true,
    token: String(r.body.token ?? link.token),
    generatedAt: String(r.body.generatedAt ?? ''),
    expiresAt: String(r.body.expiresAt ?? ''),
    ...(typeof r.body.replacesLinkId === 'string' ? { replacesLinkId: r.body.replacesLinkId } : {}),
  };
}

/** Atomically register a same-day walk-in; there is no deposit on this path. */
export function createWalkInIntent(booking: Booking, customer?: CustomerRecord) {
  return postIntent('/api/intents/create-walkin', { booking, ...(customer ? {customer} : {}) });
}

// ── send-dm ──────────────────────────────────────────────────────────────────

/**
 * `POST /api/intents/send-dm` (tv-isee) — a human, composer-originated
 * Instagram DM send via HookChat. Never call from automation: `path` must
 * match the thread's current `dmSendMode` (24h reply / 24h-7d human_agent /
 * 7d+ handoff), and `human_agent` sends must be written or reviewed by a
 * person. The caller mints `clientMessageId` (`crypto.randomUUID()`) ONCE per
 * send action and reuses it on a retry of the SAME action — the server is
 * idempotent on it, and a replay of an in-flight send can come back with
 * `deliveryStatus: 'sending'` rather than a terminal status.
 */
export type SendDmResult =
  | {
      ok: true;
      entry: ConversationEntry;
      deliveryStatus: NonNullable<ConversationEntry['deliveryStatus']>;
      error: string | null;
    }
  | { ok: false; status: number; error: string; message?: string };

export async function sendDmIntent(payload: {
  customerId: string;
  text: string;
  path: 'reply' | 'human_agent' | 'handoff';
  clientMessageId: string;
  templateId?: string;
  depositId?: string;
}): Promise<SendDmResult> {
  let res: Response;
  try {
    res = await fetch('/api/intents/send-dm', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.ok) {
    return {
      ok: true,
      entry: body.entry as ConversationEntry,
      deliveryStatus: body.deliveryStatus as NonNullable<ConversationEntry['deliveryStatus']>,
      error: (body.error as string | null) ?? null,
    };
  }
  return {
    ok: false,
    status: res.status,
    error: (body.error as string) ?? `http-${res.status}`,
    ...(body.message ? { message: String(body.message) } : {}),
  };
}

/**
 * Plain-language copy for a send-dm failure (never the raw server code).
 * Shared by every surface that calls sendDmIntent (the composer and the
 * suggested-reply card), so the wording never drifts between them (tv-isee).
 */
export function sendErrorCopy(res: { status: number; error: string }): string {
  if (res.error === 'no-dm-identity') return "This customer isn't linked to an Instagram conversation yet.";
  if (res.error === 'hookchat-not-configured') return "Instagram sending isn't set up here.";
  if (res.status === 0 || res.error === 'network') return "Couldn't reach the server. Try again.";
  return "Couldn't send. Try again.";
}

// ── link-dm-identity ─────────────────────────────────────────────────────────

/**
 * `POST /api/intents/link-dm-identity` (tv-isee) — merges a HookChat-created
 * lead's Instagram identity onto an existing customer, moving the lead's
 * conversation rows across in one transaction. `movedRows` is how many rows
 * moved; `leadDeleted` is true when the now-empty lead record was removed.
 */
export type LinkDmIdentityResult =
  | { ok: true; toCustomerId: string; movedRows: number; leadDeleted: boolean }
  | { ok: false; status: number; error: string };

export async function linkDmIdentityIntent(
  fromCustomerId: string,
  toCustomerId: string,
): Promise<LinkDmIdentityResult> {
  let res: Response;
  try {
    res = await fetch('/api/intents/link-dm-identity', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ fromCustomerId, toCustomerId }),
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.ok) {
    return {
      ok: true,
      toCustomerId: String(body.toCustomerId),
      movedRows: Number(body.movedRows ?? 0),
      leadDeleted: body.leadDeleted === true,
    };
  }
  return { ok: false, status: res.status, error: (body.error as string) ?? `http-${res.status}` };
}
